import type { FetchResult } from '@apollo/client';

import { TaskList } from '../rxdb/taskList/queryBuilder';
import { InspectorRole } from '../utilities/enums';
import { client, gql } from './Client';

interface IAssignInspectorResponse {
  data: {
    mobileFIAddOrUpdateInspection: {
      id: string;
      pk_uuid: string;
      status: string;
    };
  };
}

interface IAssignInspectorBulkResponse {
  data: {
    bulkMobileFIAddOrUpdateInspection: string;
  };
}

const saveFieldInspectorMutation = gql`
  mutation mobileSaveFieldInspector(
    $scheduleId: Int!
    $leadInspectorId: Int!
    $fieldInspectorId: Int!
    $clerkId: Int!
  ) {
    mobileSaveFieldInspector(
      scheduleId: $scheduleId
      leadInspectorId: $leadInspectorId
      fieldInspectorId: $fieldInspectorId
      clerkId: $clerkId
    ) {
      id
      workPackageId
      leadInspectorId
      fieldInspectorId
    }
  }
`;

const removeFieldInspectorMutation = gql`
  mutation removeFieldInspectorOnWPLevel(
    $workPackageId: Int!
    $fieldInspectorId: Int!
    $clerkId: Int!
  ) {
    removeFieldInspectorOnWPLevel(
      workPackageId: $workPackageId
      fieldInspectorId: $fieldInspectorId
      clerkId: $clerkId
    )
  }
`;

const saveAssignedInspectorMutation = gql`
  mutation mobileFIAddOrUpdateInspection(
    $role: Int!
    $MobileFieldInspectorInspectionsInput: MobileFieldInspectorInspectionsInput
  ) {
    mobileFIAddOrUpdateInspection(
      role: $role
      mobileFieldInspectorInspections: $MobileFieldInspectorInspectionsInput
    ) {
      id
      pk_uuid
      status
    }
  }
`;

const saveAssignedInspectorBulkMutation = gql`
  mutation bulkMobileFIAddOrUpdateInspection(
    $role: Int!
    $mobileFieldInspectorInspections: MobileFieldInspectorInspectionsInput
  ) {
    bulkMobileFIAddOrUpdateInspection(
      role: $role
      mobileFieldInspectorInspections: $mobileFieldInspectorInspections
    )
  }
`;

const saveFieldInspectorApi = (
  scheduleId: number,
  leadInspectorId: number,
  fieldInspectorId?: number,
  clerkId?: number,
) => {
  return client.mutate({
    mutation: saveFieldInspectorMutation,
    variables: {
      scheduleId,
      leadInspectorId,
      fieldInspectorId: fieldInspectorId ? fieldInspectorId : 0,
      clerkId: clerkId ? clerkId : 0,
    },
  });
};

const removeFieldInspectorApi = (
  workPackageId: number,
  fieldInspectorId: number,
  clerkId: number,
) => {
  return client.mutate({
    mutation: removeFieldInspectorMutation,
    variables: {
      workPackageId,
      fieldInspectorId,
      clerkId,
    },
  });
};

const assignInspector = (
  pkAssetInspectionTask: number,
  fkSchedule: number,
  fkAssetInspection: number,
  fkInspectorLead: number,
  fkInspectorField: number,
  taskDescription: string,
  isReviewedByLI: boolean,
  isReviewedByFI: boolean,
  commentInspectorLead: string,
  commentInspectorField: string,
  isActive: boolean,
  proxyInspector: string,
  fkClerk: number,
  site: number,
  discipline: number,
  uuid: string,
  taskStatus?: number | undefined,
  isDECUser = false,
  recommendations?: string,
  dateCompleted?: string | null,
  isPerformedByFI = false,
  isBulkReAssignment = false,
): Promise<FetchResult<IAssignInspectorResponse>> => {
  return client.mutate({
    mutation: saveAssignedInspectorMutation,
    variables: {
      role: isDECUser
        ? InspectorRole.DEC
        : isPerformedByFI
        ? InspectorRole.FIELD
        : InspectorRole.LEAD,
      MobileFieldInspectorInspectionsInput: {
        pkAssetInspectionTask: pkAssetInspectionTask === 0 ? undefined : pkAssetInspectionTask,
        fkSchedule,
        fkAssetInspection,
        fkInspectorLead,
        fkInspectorField,
        taskDescription,
        isReviewedByLI,
        isReviewedByFI,
        commentInspectorLead,
        commentInspectorField,
        site,
        discipline,
        isActive,
        fkInspectionTaskStatusCategory: taskStatus || 5,
        pkUuId: uuid,
        recommendations,
        dateCompleted,
        isPerformedByFI,
        isBulkReAssignment,
        clerkId: fkClerk,
        proxyInspector: fkClerk && proxyInspector ? proxyInspector : '',
      },
    },
  });
};

const assignInspectorBulk = (
  pkAssetInspectionTask: number,
  fkSchedule: number,
  fkAssetInspection: number,
  fkInspectorLead: number,
  fkInspectorField: number,
  clerkId: number,
  taskDescription: string,
  isReviewedByLI: boolean,
  isReviewedByFI: boolean,
  commentInspectorLead: string,
  commentInspectorField: string,
  isActive: boolean,
  site: number,
  discipline: number,
  uuid: string,
  taskStatus?: number | undefined,
  recommendations?: string,
  dateCompleted?: string | null,
  isPerformedByFI = false,
  isBulkReAssignment = false,
): Promise<FetchResult<IAssignInspectorBulkResponse>> => {
  return client.mutate({
    mutation: saveAssignedInspectorBulkMutation,
    variables: {
      role: isPerformedByFI
        ? clerkId
          ? InspectorRole.DEC
          : InspectorRole.FIELD
        : InspectorRole.LEAD,
      mobileFieldInspectorInspections: {
        pkAssetInspectionTask: pkAssetInspectionTask === 0 ? undefined : pkAssetInspectionTask,
        fkSchedule,
        fkAssetInspection,
        fkInspectorLead,
        fkInspectorField,
        clerkId,
        taskDescription,
        isReviewedByLI,
        isReviewedByFI,
        commentInspectorLead,
        commentInspectorField,
        site,
        discipline,
        isActive,
        fkInspectionTaskStatusCategory: taskStatus || 5,
        pkUuId: uuid,
        recommendations,
        dateCompleted,
        isPerformedByFI,
        isBulkReAssignment,
      },
    },
  });
};

const deleteTask = (taskData: TaskList) => {
  return client.mutate({
    mutation: saveAssignedInspectorMutation,
    variables: {
      role: InspectorRole.LEAD,
      MobileFieldInspectorInspectionsInput: {
        ...taskData,
        isActive: false,
        dateModified: undefined,
        _attachments: undefined,
        _rev: undefined,
        name: undefined,
        leadInspectorName: undefined,
        fieldInspectorUserId: undefined,
        siteCoordinatorUserId: undefined,
        clerkId: undefined,
        clerkName: undefined,
        proxyInspector: undefined,
      },
    },
  });
};

const bulkMobileFIAddOrUpdateInspectionInList = gql`
  mutation bulkMobileFIAddOrUpdateInspectionInList(
    $role: Int!
    $mobileFieldInspectorInspections: MobileFieldInspectorInspectionsInput
    $inspectionIdList: [Int!]
    $taskIdList: [Int!]
  ) {
    bulkMobileFIAddOrUpdateInspectionInList(
      role: $role
      mobileFieldInspectorInspections: $mobileFieldInspectorInspections
      inspectionIdList: $inspectionIdList
      taskIdList: $taskIdList
    )
  }
`;

const bulkAssignOrReassign = variables =>
  client.mutate({
    mutation: bulkMobileFIAddOrUpdateInspectionInList,
    variables,
  });

export {
  assignInspector,
  assignInspectorBulk,
  bulkAssignOrReassign,
  deleteTask,
  removeFieldInspectorApi,
  saveFieldInspectorApi,
};
